import type { ProductImageKind } from '@app/components/product-image'
import { apiFetch } from '@app/lib/api'
import type {
  BarcodeResolution,
  StockCardDetail,
  StockListResponse,
  StockSort,
} from '../../../worker/api/stock.types'

/**
 * Data layer for the stock module: typed fetch helpers + TanStack Query keys.
 * Response types come from the Worker's API contract (`worker/api/stock.types.ts`),
 * so the API and the screens share a single set of types and can't drift apart.
 */

export type {
  BarcodeResolution,
  StockBarcodeRow,
  StockCardAttribute,
  StockCardDetail,
  StockColorRow,
  StockListItem,
  StockListResponse,
  StockLocationOption,
  StockLocationQuantity,
  StockMovementRow,
  StockSizeCell,
  StockSort,
} from '../../../worker/api/stock.types'

export interface StockListParams {
  query: string
  location: string
  onlyCritical: boolean
  sort: StockSort
  page: number
  pageSize: number
}

export const STOCK_PAGE_SIZE = 50

export const stockKeys = {
  all: ['stock'] as const,
  list: (params: StockListParams) => ['stock', 'list', params] as const,
  card: (skod: string) => ['stock', 'card', skod] as const,
  barcode: (code: string) => ['stock', 'barcode', code] as const,
}

export function fetchStockList(params: StockListParams): Promise<StockListResponse> {
  const search = new URLSearchParams()
  if (params.query) search.set('query', params.query)
  if (params.location) search.set('location', params.location)
  if (params.onlyCritical) search.set('onlyCritical', '1')
  search.set('sort', params.sort)
  search.set('page', String(params.page))
  search.set('pageSize', String(params.pageSize))
  return apiFetch<StockListResponse>(`/stock?${search.toString()}`)
}

export function fetchStockCard(skod: string): Promise<StockCardDetail> {
  return apiFetch<StockCardDetail>(`/stock/${encodeURIComponent(skod)}`)
}

export function resolveBarcode(code: string): Promise<BarcodeResolution> {
  return apiFetch<BarcodeResolution>(`/stock/barcode/${encodeURIComponent(code)}`)
}

/* ── Formatting ─────────────────────────────────────────────────────────────── */

const numberFormat = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 })

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return numberFormat.format(value)
}

/*
 * API dates are factory wall-clock time and carry NO timezone
 * ('YYYY-MM-DD HH:MM'); the source columns are `timestamp without time zone`.
 * Parsing with `new Date(...)` and converting to local time would shift the
 * hours by the browser's timezone (3 hours in Istanbul's case), so the string
 * is split directly instead — the same pattern the production and orders
 * modules use.
 */

/** 'YYYY-MM-DD…' → 'dd.mm.yyyy'. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.slice(0, 10).split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

/** 'YYYY-MM-DD HH:MM' (or '…THH:MM') → 'dd.mm.yyyy HH:MM'. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const datePart = formatDate(value)
  const timePart = value.slice(11, 16)
  return /^\d{2}:\d{2}$/.test(timePart) ? `${datePart} ${timePart}` : datePart
}

/**
 * Location names carry "Modelle Mamül Depo" and take up a lot of table space;
 * every record starts with "Modelle", so we drop that prefix when shortening.
 */
export function shortLocationName(name: string): string {
  return name.replace(/^Modelle\s+/i, '')
}

/**
 * Which glyph stands in for a card's missing photo.
 *
 * `StokKart.StokSekli` is 'M' for a finished pair and 'H' for everything bought
 * in (leather, soles, lining). A card with no shape at all is drawn as a shoe:
 * the placeholder is a guess either way, and this list is mostly product.
 */
export function stockImageKind(shape: string | null): ProductImageKind {
  return shape === null || shape === 'M' ? 'shoe' : 'material'
}

/**
 * Negative on-hand is normal in this data (happens when a consumption voucher
 * is posted without a matching production receipt), so it is never hidden —
 * it's flagged with mustard instead.
 */
export function quantityColor(value: number): string {
  if (value < 0) return 'text-hardal'
  if (value === 0) return 'text-ink-muted/55'
  return 'text-ink'
}
